import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Form, Button, Radio, Input, DatePicker, Row, Col } from 'tea-component';
import { useIntl } from 'react-intl';
import TinyEditor from '@sg/components/TinyEditor';
import UploadFileList from '@sg/components/UploadFileList';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import CommonBubble from '@home/components/CommonBubble';
import chatGPTIcon from '@sg/assests/svg/chatgpt-icon.svg';
import deepSeekIcon from '@sg/assests/svg/deepseek-icon.svg';
import moment from 'moment';
import './index.scss';

interface EditMeetingModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void;
  meetingData: any | null;
  onDataChange?: (data: any) => void; // Sync modal changes back to parent for auto-save
}

const EditMeetingModal: React.FC<EditMeetingModalProps> = ({
  visible,
  onClose,
  onConfirm,
  meetingData,
  onDataChange,
}) => {
  const { formatMessage } = useIntl();

  // Use a counter to force TinyEditor remount when modal opens
  const editorKeyRef = useRef(0);

  // Form states
  const [contentType, setContentType] = useState<'text' | 'chatGPT' | 'deepSeek'>('text');
  const [summary_title, setSummary_title] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<any[]>([]);
  // const [deletePathList, setDeletePathList] = useState<string[]>([]);
  // const [isFilesReady, setIsFilesReady] = useState(true);
  const [date, setDate] = useState<any>(null);

  // Auto-calculate status based on selected date
  const selectedStatus = useMemo(() => {
    if (!date) return 'Completed';
    const today = moment().startOf('day');
    const selectedDay = moment(date).startOf('day');
    return selectedDay.isBefore(today) ? 'Completed' : 'Upcoming';
  }, [date]);

  // Pre-populate form when meetingData changes
  useEffect(() => {
    if (meetingData && visible) {
      setSummary_title(meetingData.summary_title || '');
      setDescription(meetingData.description || '');

      // Transform files from API format to UploadFileList format
      const transformedFiles = (meetingData.files || []).map((file: any) => ({
        name: file.file_name || file.name,
        path: file.url || file.path,
        format: (file.file_name || file.name)?.split('.').pop() || '',
        size: file.size || 0,
      }));
      setFiles(transformedFiles);

      setContentType('text'); // Default to text
      setDate(meetingData.date ? moment(meetingData.date) : null);

      // Increment editor key to force remount
      editorKeyRef.current += 1;
    }
  }, [meetingData, visible]);

  // Memoize TinyEditor callback to prevent infinite re-renders
  const handleDescriptionChange = useCallback((htmlContent: string) => {
    setDescription(htmlContent);
    // Trigger onDataChange for auto-save tracking
    if (onDataChange) {
      onDataChange({ description: htmlContent });
    }
  }, [onDataChange]);

  // Reset form on close
  const handleClose = () => {
    setSummary_title('');
    setDescription('');
    setFiles([]);
    // setDeletePathList([]);
    setContentType('text');
    setDate(null);
    onClose();
  };

  const handleConfirm = () => {
    // Transform files to API format if needed
    // const transformedFiles = files.map((file: any) => ({
    //   file_name: file.name || file.file_name,
    //   url: file.path || file.url,
    // }));

    const updatedData = {
      ...meetingData,
      summary_title: summary_title,
      description,
      // files: transformedFiles,
      date: date ? date.format('YYYY-MM-DD') : meetingData.date,
      status: selectedStatus === 'Completed' ? 1 : 0, // 1=completed, 0=upcoming
    };
    onConfirm(updatedData);
    handleClose();
  };

  // const handleFilesChange = (newFiles: any[]) => {
  //   setFiles(newFiles);
  // };

  // const handleFilesIsAllow = (isAllowed: boolean) => {
  //   setIsFilesReady(isAllowed);
  // };

  const memoizedFiles = useMemo(
    () => files,
    [files.length, files.map(f => `${f.path}|${f.name}`).join(',')]
  );

  if (!meetingData) return null;

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      caption={`Edit Meeting_${meetingData.studio_name || 'Unknown Studio'}`}
      size="auto"
      className="dbs-edit-meeting-modal dbs-common-modal"
    >
      <Modal.Body>
        <Form className="dbs-edit-meeting-form">
          {/* Meeting and Date in same row using Tea Grid */}
          <Row>
            <Col span={12}>
              <Form.Item label="Meeting" className="dbs-meeting-title-row">
                <Input
                  className='dbs-meeting-title-input'
                  value={meetingData.title || ''}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Date" className="dbs-meeting-title-row">
                <div className='dbs-meeting-date-wrapper'>
                  <DatePickerWrapper label="Day" className='dbs-meeting-date-picker'>
                    <DatePicker
                      placeholder="Select Date"
                      value={date}
                      onChange={(value) => {
                        setDate(value);
                        // Trigger onDataChange for auto-save tracking
                        if (onDataChange) {
                          onDataChange({ date: value ? value.format('YYYY-MM-DD') : null });
                        }
                      }}
                      overlayClassName='dbs-month-picker-overlay'
                    />
                  </DatePickerWrapper>
                  <span className='dbs-meeting-date-separator'>-</span>
                  <Input
                    value={selectedStatus}
                    disabled
                    style={{ width: '170px' }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>


          <Row className='dbs-meeting-type-row'>
            <Col span={24}>
              <Form.Item label="Type">
                {/* Type Selection */}
                <Radio.Group
                  value={contentType}
                  onChange={(value) => setContentType(value as 'text' | 'chatGPT' | 'deepSeek')}
                  className='dbs-meeting-type-radio-group'
                >
                  <Radio name="text">Text</Radio>
                  <CommonBubble content="AI capabilities are not available yet" placement="top">
                    <Radio name="chatGPT" disabled>
                      <img src={chatGPTIcon} alt="ChatGPT" style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }} />
                      ChatGPT
                    </Radio>
                  </CommonBubble>
                  <CommonBubble content="AI capabilities are not available yet" placement="top">
                    <Radio name="deepSeek" disabled>
                      <img src={deepSeekIcon} alt="DeepSeek" style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }} />
                      DeepSeek
                    </Radio>
                  </CommonBubble>
                </Radio.Group>
                {/* Meeting Summary */}
                <Input
                  value={summary_title}
                  onChange={(value) => {
                    setSummary_title(value);
                    // Trigger onDataChange for auto-save tracking
                    if (onDataChange) {
                      onDataChange({ summary_title: value });
                    }
                  }}
                  placeholder="Please enter title"
                  className='dbs-meeting-summary-title-input'
                />
                {/* Description with TinyEditor */}
                <TinyEditor
                  key={`editor-${editorKeyRef.current}`}
                  className='dbs-meeting-tinyeditor'
                  value={description}
                  getEditorContent={handleDescriptionChange}
                  placeholder="Please enter text"
                  minHeight={200}
                  maxContentHeight={300}
                  isMaxHeightScroll={true}
                  isShowToolbar={true}
                  showCharacterCount={false}
                />
                <UploadFileList
                  className='dbs-meeting-upload-list'
                  autoResizeInput={true}
                  showFileList={true}
                  fileListPosition="top"
                  uniqueId="edit-meeting-upload"
                  defaultList={memoizedFiles}
                  // getIsAllow={handleFilesIsAllow}
                  // onResult={handleFilesChange}
                  hideUploadButton={true}
                  excludeEdit={true}
                  excludeDelete={true}
                  multiple={true}
                  maxSize={5 * 1024 * 1024 * 1024} // 5GB
                  buttonTips=""
                  // setDeletePathList={setDeletePathList}
                  // deletePathList={deletePathList}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="primary"
          onClick={handleConfirm}
          // disabled={!isFilesReady}
          className="dbs-gradient-btn"
        >
          {formatMessage({ id: 'dbs_confirm' })}
        </Button>
        <Button onClick={handleClose}>
          {formatMessage({ id: 'dbs_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditMeetingModal;
